import { useRef, useEffect, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  ContentCopy,
  Download,
  Print,
  QrCode2,
  Link as LinkIcon,
  Check,
} from '@mui/icons-material'
import QRCodeStyling from 'qr-code-styling'
import { useAdventureEditorStore } from '../../../stores/adventureEditorStore'

// ====================
// QR CODE BACKGROUND CONFIGURATION
// ====================

type QrStyle = 'simple' | 'nature' | 'puzzle' | 'urban'

const QR_BACKGROUNDS: Record<QrStyle, string | null> = {
  simple: null,
  nature: '/qr-backgrounds/Nature_QR_Code.png',
  puzzle: '/qr-backgrounds/Puzzle_QR_Code.png',
  urban: '/qr-backgrounds/Urban_QR_Code.png',
}

const QR_STYLE_LABELS: Record<QrStyle, string> = {
  simple: 'Simple',
  nature: 'Nature',
  puzzle: 'Puzzle',
  urban: 'Urban',
}

// Background image dimensions and QR code positioning
const BG_WIDTH = 531
const BG_HEIGHT = 635
const QR_SIZE = 400 // QR code size for themed backgrounds
const QR_X = 65     // Horizontal position (centered in box)
const QR_Y = 68     // Vertical position (centered in box)

export function CodeManagementPanel() {
  const adventure = useAdventureEditorStore((state) => state.adventure)
  const qrRef = useRef<HTMLDivElement>(null)
  const qrCodeRef = useRef<QRCodeStyling | null>(null)
  const compositeCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [codeCopied, setCodeCopied] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)
  const [qrStyle, setQrStyle] = useState<QrStyle>('simple')
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null)
  const [compositeDataUrl, setCompositeDataUrl] = useState<string | null>(null)

  const geocacheCode = adventure?.geocacheCode || ''
  const baseUrl = window.location.origin
  const fullUrl = geocacheCode ? `${baseUrl}/code/${geocacheCode}` : ''

  // Load background image when style changes
  useEffect(() => {
    const bgPath = QR_BACKGROUNDS[qrStyle]
    if (!bgPath) {
      setBackgroundImage(null)
      return
    }

    const img = new Image()
    img.onload = () => setBackgroundImage(img)
    img.onerror = () => {
      console.error('Failed to load QR background:', bgPath)
      setBackgroundImage(null)
    }
    img.src = bgPath
  }, [qrStyle])

  // Generate QR code
  useEffect(() => {
    if (!qrRef.current || !fullUrl) return

    // Create the HA logo as SVG
    const logoSvg = `
      <svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
        <rect width="80" height="80" rx="12" fill="#b8762e"/>
        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="36" font-weight="bold"
              fill="white" text-anchor="middle" dominant-baseline="middle">HA</text>
      </svg>
    `
    const logoDataUrl = `data:image/svg+xml;base64,${btoa(logoSvg)}`

    // Use larger QR for themed backgrounds
    const qrSize = qrStyle === 'simple' ? 300 : QR_SIZE

    qrCodeRef.current = new QRCodeStyling({
      width: qrSize,
      height: qrSize,
      data: fullUrl,
      margin: qrStyle === 'simple' ? 10 : 0,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'H',
      },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.3,
        margin: 4,
      },
      dotsOptions: {
        type: 'rounded',
        color: '#2d5016',
      },
      backgroundOptions: {
        color: qrStyle === 'simple' ? '#f5f1e8' : 'transparent',
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
        color: '#2d5016',
      },
      cornersDotOptions: {
        type: 'dot',
        color: '#2d5016',
      },
      image: logoDataUrl,
    })

    qrRef.current.innerHTML = ''
    qrCodeRef.current.append(qrRef.current)

    return () => {
      if (qrRef.current) {
        qrRef.current.innerHTML = ''
      }
    }
  }, [fullUrl, qrStyle])

  // Create composite image when QR and background are ready
  const createComposite = useCallback(() => {
    if (qrStyle === 'simple' || !backgroundImage || !qrRef.current) {
      setCompositeDataUrl(null)
      return
    }

    const qrCanvas = qrRef.current.querySelector('canvas')
    if (!qrCanvas) return

    // Create composite canvas
    const canvas = document.createElement('canvas')
    canvas.width = BG_WIDTH
    canvas.height = BG_HEIGHT
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Draw background
    ctx.drawImage(backgroundImage, 0, 0, BG_WIDTH, BG_HEIGHT)

    // Draw QR code at the correct position
    ctx.drawImage(qrCanvas, QR_X, QR_Y, QR_SIZE, QR_SIZE)

    // Save composite data URL
    setCompositeDataUrl(canvas.toDataURL('image/png'))
    compositeCanvasRef.current = canvas
  }, [qrStyle, backgroundImage])

  // Trigger composite creation when dependencies change
  useEffect(() => {
    // Small delay to ensure QR canvas is rendered
    const timer = setTimeout(createComposite, 100)
    return () => clearTimeout(timer)
  }, [createComposite, fullUrl])

  const handleCopyCode = async () => {
    if (!geocacheCode) return
    try {
      await navigator.clipboard.writeText(geocacheCode)
      setCodeCopied(true)
      setTimeout(() => setCodeCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  const handleCopyUrl = async () => {
    if (!fullUrl) return
    try {
      await navigator.clipboard.writeText(fullUrl)
      setUrlCopied(true)
      setTimeout(() => setUrlCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  const handleDownloadQR = () => {
    if (qrStyle !== 'simple' && compositeCanvasRef.current) {
      // Download the composite image
      const link = document.createElement('a')
      link.download = `geocache-${geocacheCode}-${qrStyle}.png`
      link.href = compositeCanvasRef.current.toDataURL('image/png')
      link.click()
    } else if (qrCodeRef.current) {
      // Download simple QR code
      qrCodeRef.current.download({
        name: `geocache-${geocacheCode}`,
        extension: 'png',
      })
    }
  }

  const handlePrintQR = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    let imgData: string
    let imgWidth: string
    let imgHeight: string

    if (qrStyle !== 'simple' && compositeDataUrl) {
      // Use composite image for themed backgrounds
      imgData = compositeDataUrl
      imgWidth = `${BG_WIDTH}px`
      imgHeight = `${BG_HEIGHT}px`
    } else if (qrRef.current) {
      // Use simple QR code
      const canvas = qrRef.current.querySelector('canvas')
      if (!canvas) {
        printWindow.close()
        return
      }
      imgData = canvas.toDataURL('image/png')
      imgWidth = '300px'
      imgHeight = '300px'
    } else {
      printWindow.close()
      return
    }

    // For themed backgrounds, don't show the title/code overlay
    const showOverlay = qrStyle === 'simple'

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Geocache QR Code - ${adventure?.title || 'Geocache'}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            .container {
              text-align: center;
            }
            h1 {
              color: #2d5016;
              margin-bottom: 10px;
            }
            .code {
              font-size: 24px;
              font-weight: bold;
              color: #333;
              letter-spacing: 2px;
              margin: 10px 0 20px;
            }
            img {
              max-width: ${imgWidth};
              max-height: ${imgHeight};
              border-radius: 8px;
            }
            .url {
              color: #666;
              font-size: 12px;
              margin-top: 15px;
              word-break: break-all;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            ${showOverlay ? `<h1>${adventure?.title || 'Geocache'}</h1>` : ''}
            ${showOverlay ? `<div class="code">${geocacheCode}</div>` : ''}
            <img src="${imgData}" alt="QR Code" />
            ${showOverlay ? `<div class="url">${fullUrl}</div>` : ''}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  if (!geocacheCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Geocache code will be generated when the adventure is saved.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QrCode2 color="primary" />
        QR Code & Share Code
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Share this code with participants to let them join. Print the QR code and place it at the adventure location.
      </Typography>

      {/* Geocache Code */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Share Code
        </Typography>
        <TextField
          fullWidth
          value={geocacheCode}
          InputProps={{
            readOnly: true,
            sx: {
              fontFamily: 'monospace',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              letterSpacing: 2,
              textAlign: 'center',
            },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={codeCopied ? 'Copied!' : 'Copy code'}>
                  <IconButton onClick={handleCopyCode} color={codeCopied ? 'success' : 'default'}>
                    {codeCopied ? <Check /> : <ContentCopy />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Full URL */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          <LinkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Share Link
        </Typography>
        <TextField
          fullWidth
          value={fullUrl}
          InputProps={{
            readOnly: true,
            sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={urlCopied ? 'Copied!' : 'Copy URL'}>
                  <IconButton onClick={handleCopyUrl} color={urlCopied ? 'success' : 'default'}>
                    {urlCopied ? <Check /> : <ContentCopy />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* QR Code */}
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          QR Code Style
        </Typography>

        {/* Style Selector */}
        <ToggleButtonGroup
          value={qrStyle}
          exclusive
          onChange={(_, value) => value && setQrStyle(value as QrStyle)}
          sx={{ mb: 3 }}
        >
          {(Object.keys(QR_BACKGROUNDS) as QrStyle[]).map((style) => (
            <ToggleButton
              key={style}
              value={style}
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {QR_BACKGROUNDS[style] ? (
                <Box
                  component="img"
                  src={QR_BACKGROUNDS[style]!}
                  alt={QR_STYLE_LABELS[style]}
                  sx={{
                    width: 50,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 50,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.200',
                    borderRadius: 1,
                  }}
                >
                  <QrCode2 sx={{ fontSize: 32, color: 'grey.600' }} />
                </Box>
              )}
              <Typography variant="caption">
                {QR_STYLE_LABELS[style]}
              </Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scan this QR code to join the adventure
        </Typography>

        {/* QR Code Preview */}
        {qrStyle === 'simple' ? (
          <Box
            ref={qrRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              my: 3,
              '& canvas': {
                borderRadius: 2,
                boxShadow: 2,
              },
            }}
          />
        ) : (
          <>
            {/* Hidden QR ref for generating the QR canvas */}
            <Box ref={qrRef} sx={{ display: 'none' }} />
            {/* Show composite preview */}
            {compositeDataUrl ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  my: 3,
                }}
              >
                <Box
                  component="img"
                  src={compositeDataUrl}
                  alt="QR Code Preview"
                  sx={{
                    maxWidth: 300,
                    height: 'auto',
                    borderRadius: 2,
                    boxShadow: 2,
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  my: 3,
                  height: 300,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                }}
              >
                <Typography color="text.secondary">
                  Loading background...
                </Typography>
              </Box>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadQR}
          >
            Download PNG
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrintQR}
          >
            Print
          </Button>
        </Box>
      </Paper>

      {/* Instructions */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Tip:</strong> Print this QR code and laminate it for weather protection.
          Place it where participants can easily scan it to join the adventure.
        </Typography>
      </Alert>
    </Box>
  )
}
